import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import { Printer, ExternalLink, Download, ArrowLeft, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function PublicInvoice() {
  const { invoiceNo } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invoice/${invoiceNo}`);
        if (!cancelled) setData(data);
      } catch (e) {
        console.error("[PublicInvoice] load failed", e);
        if (!cancelled) setErr(e.response?.data?.detail || "Invoice not found");
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceNo]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="ldc-glass p-8 text-center max-w-md">
        <div className="font-display text-2xl">Invoice not found</div>
        <div className="text-white/50 mt-2 text-sm">{err}</div>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-[#fbce18] text-sm"><ArrowLeft size={14}/> Home</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading…</div>;

  const link = data.payment_link;
  const p = data.payment;
  const b = data.branch;
  const qrUrl = `${API}/invoice/${invoiceNo}/qr`;

  // Prefer payment.status (captured / refunded) over the link.status ("paid") when a payment exists
  const status = p?.status === "refunded" ? "refunded" : (link?.status);
  const canPay = link?.status === "pending" && link?.payment_link;

  return (
    <div className="relative min-h-screen">
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center justify-between no-print">
          <Link to="/" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14}/> Home</Link>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="px-4 py-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid="inv-print"><Printer size={14}/> Print</button>
            <a href={`${API}/invoice/${invoiceNo}/pdf`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid="inv-pdf"><FileDown size={14}/> PDF</a>
            {canPay && <a href={link.payment_link} target="_blank" rel="noreferrer" className="ldc-btn-primary px-5 py-2 rounded-md inline-flex items-center gap-2 text-sm" data-testid="inv-pay"><ExternalLink size={14}/> Pay Now</a>}
          </div>
        </div>

        <div className="ldc-glass p-8 mt-6" data-testid="invoice-page">
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-start gap-3">
                {b?.logo || link?.logo_link ? (
                  <div className="p-2 bg-white/5 border border-white/10 rounded" style={{ minWidth: "72px", minHeight: "56px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={b?.logo || link?.logo_link}
                      alt=""
                      style={{ objectFit: "contain", width: "auto", height: "auto", maxWidth: "200px", maxHeight: "120px" }}
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded bg-[#fbce18] text-black font-black font-display text-2xl flex items-center justify-center">L</div>
                )}
                <div className="pt-1">
                  <div className="font-display text-xl font-semibold">{b?.name || link?.branch || "LostDRKC Payments"}</div>
                  {b?.gstin && <div className="text-xs text-white/50">GSTIN: {b.gstin}</div>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Invoice</div>
              <div className="mt-1 font-mono text-2xl text-[#fbce18]">{invoiceNo}</div>
              <div className="mt-2"><span className={statusBadgeClass(status)}>{status}</span></div>
            </div>
          </div>

          <div className="ldc-divider my-6"></div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Billed to</div>
              <div className="mt-2 text-lg">{link.customer_name}</div>
              <div className="text-sm text-white/60">{link.email}</div>
              <div className="text-sm text-white/60">{link.phone}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Details</div>
              <div className="mt-2 text-sm text-white/70">Purpose: <span className="text-white">{link.purpose}</span></div>
              <div className="text-sm text-white/70">Created: <span className="text-white">{formatDate(link.created_at)}</span></div>
              {link.paid_at && <div className="text-sm text-white/70">Paid: <span className="text-white">{formatDate(link.paid_at)}</span></div>}
              {link.expiry_at && <div className="text-sm text-white/70">Expiry: <span className="text-white">{formatDate(link.expiry_at)}</span></div>}
            </div>
          </div>

          <div className="mt-8 ldc-glass p-5">
            <div className="grid grid-cols-2 gap-4">
              <Row label="Subtotal" value={formatINR(link.amount, link.currency)} />
              <Row label="Amount Due" value={formatINR(link.amount, link.currency)} bold accent />
              {p && (
                <>
                  <Row label="Amount Paid" value={formatINR(p.amount)} />
                  <Row label="Payment Method" value={(p.method || "—").toUpperCase()} />
                  <Row label="Payment ID" value={p.payment_id} mono />
                  {p.rrn && <Row label="RRN" value={p.rrn} mono />}
                  {p.vpa && <Row label="UPI ID" value={p.vpa} mono />}
                  <Row label="Fee" value={formatINR(p.fee)} />
                  <Row label="GST" value={formatINR(p.tax)} />
                </>
              )}
            </div>
          </div>

          {link.message && (
            <div className="mt-6">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-1">Message</div>
              <div className="text-sm text-white/70">{link.message}</div>
            </div>
          )}

          <div className="mt-6 p-4 rounded border border-white/8 bg-white/[0.03]">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80 mb-1">Refund Policy</div>
            <div className="text-xs text-white/60 leading-relaxed">
              Refunds are available only within <span className="text-white/90 font-semibold">2 days (48 hours)</span> of successful payment and are subject to admin approval. Please reach out to the merchant to request a refund.
            </div>
          </div>

          <div className="mt-8 flex items-end justify-between flex-wrap gap-6">
            <div className="text-xs text-white/40">
              This invoice is generated by LostDRKC Payments. Keep for your records.
            </div>
            <div className="text-right">
              <img src={qrUrl} alt="QR" className="w-28 h-28 bg-white p-2 rounded" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Scan to open</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          .no-print { display: none !important; }
          .ldc-glass { background: white !important; box-shadow: none !important; border: 1px solid #ddd !important; color: black !important; }
          .text-white, .text-white\\/60, .text-white\\/70, .text-white\\/80 { color: black !important; }
        }
      `}</style>
    </div>
  );
}

function Row({ label, value, bold, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40">{label}</div>
      <div className={`mt-1 ${bold ? "text-lg font-semibold" : "text-sm"} ${mono ? "font-mono" : ""} ${accent ? "text-[#fbce18]" : ""}`}>{value}</div>
    </div>
  );
}
